<template>
  <!-- キャストに見せる画面のため、背後の他キャストの情報が見えないようフルスクリーンで覆う -->
  <v-dialog
    v-model="open"
    fullscreen
    transition="dialog-bottom-transition"
    content-class="user-statement-dialog"
  >
    <v-card
      tile
      class="user-statement-card d-flex flex-column"
      height="100%"
    >
      <!-- タブレットのステータスバー/ブラウザUIにタイトルが隠れないようセーフエリア分を空ける -->
      <v-toolbar
        flat
        dense
        class="flex-grow-0 user-statement-toolbar"
      >
        <v-toolbar-title class="text-subtitle-1 font-weight-bold">
          {{ userName }} / {{ date }}
        </v-toolbar-title>
        <v-spacer />
        <v-btn
          text
          :disabled="saving"
          @click="open = false"
        >
          <v-icon
            small
            left
          >
            {{ icons.mdiClose }}
          </v-icon>
          閉じる
        </v-btn>
      </v-toolbar>
      <v-divider />

      <v-card-text class="flex-grow-1 overflow-y-auto pt-4">
        <div class="statement-content mx-auto">
          <div
            v-if="loadingOptions"
            class="d-flex justify-center py-8"
          >
            <v-progress-circular
              indeterminate
              size="32"
            />
          </div>

          <template v-else>
            <v-select
              v-model="selectedKey"
              :items="options"
              item-text="label"
              item-value="key"
              label="表示内容"
              dense
              outlined
              hide-details
              class="mb-3"
            />

            <v-checkbox
              v-model="withSignature"
              dense
              hide-details
              class="mt-0 mb-3"
              label="受領サインをもらう"
            />

            <!-- 二重発行を防ぐため、この営業日にこのキャストの伝票を印刷済みかを示す。
                 印刷内容そのものはグループポータルで確認できる -->
            <v-alert
              v-if="printedCount > 0"
              dense
              text
              type="info"
              class="text-caption mb-3"
            >
              <div>
                この営業日は<strong>印刷済み</strong>です({{ printedCount }}回<template v-if="lastPrintedAtText">
                  、最終 {{ lastPrintedAtText }}
                </template>)
              </div>
              <div>印刷した内容はグループポータルの「日払いサイン」から確認できます。</div>
            </v-alert>

            <div
              v-if="loadingPreview"
              class="d-flex justify-center py-8"
            >
              <v-progress-circular
                indeterminate
                size="32"
              />
            </div>

            <!-- 印字イメージ(サーバーのSDPビルダーと同じ行構成)。見たままが印刷される -->
            <template v-else-if="preview">
              <div class="d-flex justify-center">
                <!-- 行構成・罫線はサーバーのSDPビルダー(UserStatementSections)と対応させる -->
                <div class="paper">
                  <div class="paper-rule-double" />
                  <div class="paper-title">
                    {{ paperTitle }}
                  </div>
                  <div class="paper-rule-double" />
                  <div class="paper-center">
                    {{ preview.clubName }}
                  </div>
                  <div class="paper-blank" />
                  <div class="paper-lr">
                    <span>対象日</span><span>{{ preview.targetDate }}</span>
                  </div>
                  <div class="paper-lr">
                    <span>氏名</span><span>{{ preview.userName }} 様</span>
                  </div>
                  <template v-if="workTimecards.length > 0">
                    <div
                      v-for="(timecard, index) in workTimecards"
                      :key="`work-${index}`"
                      class="paper-lr"
                    >
                      <span>勤務</span><span>{{ formatTime(timecard.startAt) }} ～ {{ formatTime(timecard.leaveAt) }}</span>
                    </div>
                    <div class="paper-lr">
                      <span>実働</span><span>{{ totalWorkingMinutes }}分</span>
                    </div>
                  </template>
                  <div
                    v-else
                    class="paper-lr"
                  >
                    <span>勤務</span><span>未打刻</span>
                  </div>
                  <div class="paper-rule-dotted" />
                  <div class="paper-heading">
                    【 {{ isPayslip ? '明細' : '内訳' }} 】
                  </div>
                  <template v-for="(row, index) in preview.rows">
                    <div
                      v-if="row.section"
                      :key="`row-${index}`"
                      class="paper-section"
                    >
                      {{ row.label }}
                    </div>
                    <div
                      v-else
                      :key="`row-${index}`"
                      class="paper-lr"
                    >
                      <span>{{ row.label }}</span><span>{{ formatValue(row.value) }}</span>
                    </div>
                  </template>
                  <p
                    v-if="preview.rows.length === 0"
                    class="text-center mb-0"
                  >
                    表示できる明細がありません
                  </p>
                  <template v-if="isAdvanceOnly">
                    <div class="paper-rule-dotted" />
                    <div class="paper-heading">
                      【 日払い額 】
                    </div>
                    <div class="paper-amount">
                      ￥{{ formatValue(preview.amount) }}
                    </div>
                  </template>
                  <div class="paper-rule-double" />
                  <template v-if="withSignature">
                    <div class="paper-blank" />
                    <div
                      v-if="isPayslip"
                      class="paper-note"
                    >
                      上記金額を受け取りました。
                    </div>
                    <div class="paper-blank" />
                    <div class="paper-heading">
                      【 受領サイン 】
                    </div>
                    <img
                      v-if="signatureDataUrl"
                      :src="signatureDataUrl"
                      alt="サイン"
                      class="paper-signature"
                    >
                    <div
                      v-else
                      class="paper-sign-placeholder"
                    >
                      下の枠にサインしてください
                    </div>
                    <div class="paper-rule-double" />
                  </template>
                </div>
              </div>

              <v-alert
                v-if="isPayslip && preview.amount <= 0"
                dense
                text
                type="warning"
                class="text-caption mt-3 mb-0"
              >
                この営業日の日払い額が0円です。集計が未実行の可能性があります。
              </v-alert>

              <!-- サイン入力欄。紙イメージ内に収めると狭くなるため、書きやすさ優先で下に大きく出す -->
              <template v-if="withSignature">
                <div class="d-flex justify-space-between align-center mt-4 mb-1">
                  <span class="text-subtitle-2">受領サイン</span>
                  <v-btn
                    text
                    x-small
                    @click="clearSignature"
                  >
                    クリア
                  </v-btn>
                </div>
                <div class="signature-pad-wrapper">
                  <canvas
                    ref="signatureCanvas"
                    class="signature-pad-canvas"
                  />
                </div>
                <p class="text-caption text--secondary mb-0 mt-1">
                  内容を確認のうえ、枠内にサインしてください
                </p>
              </template>
            </template>
          </template>
        </div>
      </v-card-text>
      <v-divider />

      <v-card-actions class="flex-grow-0 user-statement-actions">
        <v-btn
          v-if="printable"
          text
          :loading="printing"
          :disabled="loadingPreview || !preview"
          @click="printSlip"
        >
          <v-icon
            small
            left
          >
            {{ icons.mdiPrinter }}
          </v-icon>
          印刷
        </v-btn>
        <v-spacer />
        <!-- 受領記録の保存は日払い明細のときのみ(成績はサインしても印刷のみ) -->
        <v-btn
          v-if="isPayslip && withSignature"
          color="primary"
          large
          :loading="saving"
          :disabled="loadingPreview || !preview"
          @click="save"
        >
          サインを保存
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import {
  ref, computed, watch, nextTick, onBeforeUnmount, getCurrentInstance,
} from '@vue/composition-api'
import { mdiClose, mdiPrinter } from '@mdi/js'
import { format, parseISO } from 'date-fns'
import SignaturePad from 'signature_pad'
import AdvancePaymentConfirmationApi from '@/api/admin/AdvancePaymentConfirmation'
import PerformanceReportApi from '@/api/admin/PerformanceReport'
import canvasToMonoRaster from '@/utils/canvas-to-mono-raster'

// 表示内容の選択肢。日払い明細2種は固定、成績は所属集計表ごとに動的生成する
const PAYSLIP_OPTIONS = [
  { key: 'payslip:advance_only', label: '日払い明細(日払いのみ)' },
  { key: 'payslip:full_breakdown', label: '日払い明細(全内訳)' },
]

/**
 * 日払い明細・成績の統合ダイアログ。
 * 「表示内容」の選択と「受領サインをもらう」チェックで、日払い受領サイン/成績印刷の
 * 全ケースを1画面で扱う。画面本体が印字イメージそのものなので別途プレビューは不要。
 * 明細はサーバー確定のスナップショットで、送信するのは署名画像のみ。
 */
export default {
  name: 'UserStatementDialog',
  model: {
    prop: 'value',
    event: 'input',
  },
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    userId: {
      type: Number,
      default: null,
    },
    userName: {
      type: String,
      default: '',
    },
    date: {
      type: String,
      required: true,
    },
  },
  setup(props, { emit }) {
    const vm = getCurrentInstance().proxy

    const loadingOptions = ref(false)
    const loadingPreview = ref(false)
    const saving = ref(false)
    const printing = ref(false)
    // 集計表一覧の取得失敗で印刷ボタンが消えないよう、プレビュー由来のフラグを優先する
    const optionsPrintable = ref(false)
    const aggregations = ref([])
    const selectedKey = ref(PAYSLIP_OPTIONS[0].key)
    // 既定はサインあり(日払いの受領サインが主用途。不要なときだけ外す)
    const withSignature = ref(true)
    const preview = ref(null)
    const signatureDataUrl = ref(null)
    const signatureCanvas = ref(null)

    let signaturePad = null

    const open = computed({
      get: () => props.value,
      set: val => emit('input', val),
    })

    const options = computed(() => [
      ...PAYSLIP_OPTIONS,
      ...aggregations.value.map(aggregation => ({
        key: `report:${aggregation.id}`,
        label: `成績: ${aggregation.name}`,
      })),
    ])

    const isPayslip = computed(() => selectedKey.value.startsWith('payslip:'))
    const isAdvanceOnly = computed(() => selectedKey.value === 'payslip:advance_only')
    const displayMode = computed(() => (isAdvanceOnly.value ? 'advance_only' : 'full_breakdown'))
    const aggregationId = computed(() => (isPayslip.value ? null : Number(selectedKey.value.split(':')[1])))
    // 印刷可否はプレビュー(表示中の伝票)由来を優先し、無ければ集計表一覧のフラグにフォールバック
    const printable = computed(() => preview.value?.printable ?? optionsPrintable.value)
    // 印刷済みの表示(二重発行の防止用)。印刷のたびにプレビューを取り直して最新化する
    const printedCount = computed(() => preview.value?.printStatus?.count || 0)
    const lastPrintedAtText = computed(() => {
      const iso = preview.value?.printStatus?.lastPrintedAt
      if (!iso) return ''

      return format(parseISO(iso), 'M/d HH:mm')
    })
    const workTimecards = computed(() => preview.value?.work?.timecards || [])
    const totalWorkingMinutes = computed(() => preview.value?.work?.totalWorkingMinutes || 0)
    const paperTitle = computed(() => (isPayslip.value ? '日払い明細' : preview.value?.aggregationName || '成績'))

    const hasSignature = () => signaturePad && !signaturePad.isEmpty()
    const signaturePayload = () => (hasSignature() ? canvasToMonoRaster(signatureCanvas.value) : null)

    // 署名canvasは表示後に実サイズへ合わせて初期化する(高DPI対応)
    const initSignaturePad = async () => {
      await nextTick()
      const canvas = signatureCanvas.value
      if (!canvas) return

      const ratio = Math.max(window.devicePixelRatio || 1, 1)
      canvas.width = canvas.offsetWidth * ratio
      canvas.height = canvas.offsetHeight * ratio
      canvas.getContext('2d').scale(ratio, ratio)

      if (signaturePad) signaturePad.off()
      signaturePad = new SignaturePad(canvas, { backgroundColor: 'rgb(255, 255, 255)' })
      // 紙イメージ内のサインは描画のたびに反映する
      signaturePad.addEventListener('endStroke', () => {
        signatureDataUrl.value = canvas.toDataURL('image/png')
      })
    }

    const clearSignature = () => {
      if (signaturePad) signaturePad.clear()
      signatureDataUrl.value = null
    }

    const fetchPreview = async () => {
      loadingPreview.value = true
      try {
        preview.value = isPayslip.value
          ? await AdvancePaymentConfirmationApi.getPreview({
            userId: props.userId,
            targetDate: props.date,
            displayMode: displayMode.value,
          })
          : await PerformanceReportApi.getPreview({
            userId: props.userId,
            targetDate: props.date,
            aggregationId: aggregationId.value,
          })
      } catch (error) {
        vm.$toast.error('明細の取得に失敗しました')
        open.value = false
      } finally {
        loadingPreview.value = false
      }
    }

    const fetchOptions = async () => {
      loadingOptions.value = true
      try {
        const data = await PerformanceReportApi.getAggregations({ userId: props.userId })
        aggregations.value = data.aggregations
        optionsPrintable.value = data.printable
      } catch (error) {
        // 集計表が取れなくても日払い明細は表示できるため、選択肢を空にして継続する
        // (印刷可否はプレビュー側のフラグで判定されるため、ここでは触らない)
        aggregations.value = []
      } finally {
        loadingOptions.value = false
      }
    }

    const printSlip = async () => {
      printing.value = true
      try {
        const signature = withSignature.value ? signaturePayload() : null
        if (isPayslip.value) {
          await AdvancePaymentConfirmationApi.print({
            userId: props.userId,
            targetDate: props.date,
            displayMode: displayMode.value,
            signature,
          })
        } else {
          await PerformanceReportApi.print({
            userId: props.userId,
            targetDate: props.date,
            aggregationId: aggregationId.value,
            signature,
          })
        }
        vm.$toast.info('印刷を開始しました')
        // 「印刷済み」表示を最新化する(サインは維持したいのでcanvasは初期化しない)
        await fetchPreview()
      } catch (error) {
        const message = error?.response?.data?.errors?.join?.('\n')
        vm.$toast.error(message || '印刷に失敗しました')
      } finally {
        printing.value = false
      }
    }

    const save = async () => {
      if (!hasSignature()) {
        vm.$toast.error('サインを入力してください')

        return
      }

      saving.value = true
      try {
        const signatureImage = await new Promise(resolve => {
          signatureCanvas.value.toBlob(blob => resolve(blob), 'image/png')
        })
        await AdvancePaymentConfirmationApi.create({
          userId: props.userId,
          targetDate: props.date,
          displayMode: displayMode.value,
          signatureImage,
        })
        vm.$toast.info('受領サインを保存しました')
        emit('saved')
        open.value = false
      } catch (error) {
        const message = error?.response?.data?.errors?.join?.('\n')
        vm.$toast.error(message || '受領サインの保存に失敗しました')
      } finally {
        saving.value = false
      }
    }

    const disposeSignaturePad = () => {
      if (!signaturePad) return

      signaturePad.off()
      signaturePad = null
    }

    // 初期化中は selectedKey / withSignature のwatcherを止める(open時のリセット代入で
    // プレビューが二重フェッチされるのを防ぐ)
    const initializing = ref(false)

    watch(open, async isOpen => {
      if (!isOpen || !props.userId) {
        disposeSignaturePad()
        signatureDataUrl.value = null

        return
      }

      initializing.value = true
      selectedKey.value = PAYSLIP_OPTIONS[0].key
      withSignature.value = true
      preview.value = null
      signatureDataUrl.value = null
      await fetchOptions()
      await fetchPreview()
      // 既定はサイン無しでcanvasが描画されないため、チェックONのときだけ初期化する
      if (withSignature.value) await initSignaturePad()
      initializing.value = false
    })

    // 表示内容の切替でcanvasが再マウントされ、署名パッドは新インスタンスになる(=書いた内容は失われる)。
    // 紙イメージ側のサインも必ず消す(見えているのに未署名、というズレを防ぐ)
    watch(selectedKey, async () => {
      if (!open.value || initializing.value) return

      signatureDataUrl.value = null
      await fetchPreview()
      if (withSignature.value) await initSignaturePad()
    })

    watch(withSignature, async enabled => {
      if (!open.value || initializing.value) return

      signatureDataUrl.value = null
      if (enabled) {
        await initSignaturePad()
      } else {
        // canvasがv-ifでDOMから外れるため、リスナを残さないよう破棄する
        disposeSignaturePad()
      }
    })

    onBeforeUnmount(disposeSignaturePad)

    return {
      open,
      loadingOptions,
      loadingPreview,
      saving,
      printing,
      printable,
      printedCount,
      lastPrintedAtText,
      options,
      selectedKey,
      withSignature,
      preview,
      signatureDataUrl,
      signatureCanvas,
      workTimecards,
      totalWorkingMinutes,
      paperTitle,
      isPayslip,
      isAdvanceOnly,
      clearSignature,
      printSlip,
      save,
      formatTime: iso => (iso ? format(parseISO(iso), 'HH:mm') : '-'),
      formatValue: value => {
        if (value === null || value === undefined || value === '') return '-'

        return typeof value === 'number' ? value.toLocaleString() : String(value)
      },
      icons: {
        mdiClose,
        mdiPrinter,
      },
    }
  },
}
</script>

<style scoped>
/* 背景・文字色はテーマ(ライト/ダーク)に追従させる。
   紙イメージだけは実際のレシートに合わせて白地・黒文字で固定する。 */
.user-statement-card {
  background-color: var(--v-background-base, #f4f5fa);
}

.theme--dark .user-statement-card,
.theme--dark.user-statement-card {
  background-color: #28243d;
}

/* タブレット(iPad等)のステータスバー・ホームインジケータ領域を避ける */
.user-statement-toolbar {
  padding-top: env(safe-area-inset-top);
}

.user-statement-actions {
  padding-bottom: calc(8px + env(safe-area-inset-bottom));
}

.statement-content {
  max-width: 420px;
}

.paper {
  width: 280px;
  background: #fff;
  color: #212121;
  font-family: 'Courier New', 'Osaka-Mono', 'MS Gothic', monospace;
  font-size: 12px;
  line-height: 1.7;
  padding: 14px 12px 18px;
  border: 1px solid #e0e0e0;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
}

.paper-title {
  text-align: center;
  font-size: 17px;
  font-weight: 700;
  letter-spacing: 1px;
  margin: 3px 0;
}

.paper-center {
  text-align: center;
}

.paper-section {
  font-weight: 700;
  padding-left: 4px;
}

.paper-note {
  padding-left: 4px;
}

/* セクション見出し(【 】)。印字側の section_heading に対応 */
.paper-heading {
  font-weight: 700;
  padding-left: 4px;
  margin: 2px 0;
}

.paper-lr {
  display: flex;
  justify-content: space-between;
  gap: 8px;
  /* 印字側の行頭・行末インデントに合わせる */
  padding: 0 4px;
}

.paper-lr span:last-child {
  text-align: right;
  word-break: break-all;
}

.paper-amount {
  text-align: right;
  font-size: 17px;
  font-weight: 700;
  padding-right: 4px;
}

/* 見出しの区切り(印字の二重線 ═ に対応) */
.paper-rule-double {
  border-top: 3px double #616161;
  margin: 5px 0;
}

/* ブロックの区切り(印字の点線 ┈ に対応) */
.paper-rule-dotted {
  border-top: 1px dotted #9e9e9e;
  margin: 5px 0;
}

.paper-blank {
  height: 10px;
}

.paper-signature {
  display: block;
  width: 100%;
  max-height: 90px;
  object-fit: contain;
}

.paper-sign-placeholder {
  height: 56px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #bdbdbd;
  font-size: 11px;
  border: 1px dashed #e0e0e0;
}

.signature-pad-wrapper {
  border: 1px solid rgba(0, 0, 0, 0.24);
  border-radius: 4px;
  background: #fff;
}

.signature-pad-canvas {
  display: block;
  width: 100%;
  height: 180px;
  /* 署名中に画面がスクロールしないようにする(タブレットのタッチ操作対応) */
  touch-action: none;
}
</style>

<style>
/* v-dialog は body 直下に描画されるため scoped では届かない。
   アプリのヘッダー・ナビゲーション(v-app-bar / v-navigation-drawer)より確実に前面へ出し、
   タブレットでタイトルが隠れないようにする。 */
.user-statement-dialog {
  z-index: 300 !important;
}
</style>
